<script setup lang="ts">
    import type { DoceboCourseStatus } from '~/types/academy/baseTraining';
    import { getTrainingStatusBadgeType } from '~/utils/academyUtils';

    const props = defineProps<{
        status: DoceboCourseStatus | null;
    }>();

    const steps = [
        'academy.trainingProgress.start',
        'academy.trainingProgress.continue',
        'academy.trainingProgress.completed'
    ] as const;

    const activeStep = computed(() => {
        const badgeType = getTrainingStatusBadgeType(props.status);
        if (badgeType === 'completed') return 3;
        if (badgeType === 'inProgress') return 1;
        return 0;
    });
</script>

<template>
    <div
        class="training-progress"
        role="group"
        :aria-label="$t('academy.trainingProgress.ariaLabel')"
    >
        <div class="progress-track">
            <div
                class="progress-line"
                aria-hidden="true"
            >
                <div
                    class="progress-line-fill"
                    :style="{ width: `${Math.min(activeStep * 50, 100)}%` }"
                />
            </div>

            <div
                v-for="(step, index) in steps"
                :key="index"
                class="progress-step"
                :class="{
                    'progress-step--completed': index < activeStep,
                    'progress-step--active': index === activeStep,
                    'progress-step--upcoming': index > activeStep
                }"
            >
                <div class="progress-dot">
                    <Icon
                        v-if="index < activeStep"
                        name="lucide:check"
                        mode="svg"
                        size="14px"
                        class="progress-check-icon"
                    />
                </div>
            </div>
        </div>

        <div class="progress-labels">
            <span
                v-for="(step, index) in steps"
                :key="index"
                class="progress-label"
                :class="{
                    'progress-label--active': index <= activeStep
                }"
            >
                {{ $t(step) }}
            </span>
        </div>
    </div>
</template>

<style scoped>
    .training-progress {
        display: flex;
        flex-direction: column;
        gap: var(--spacing--05);
    }

    .progress-track {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .progress-line {
        position: absolute;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--gray-medium);
    }

    .progress-line-fill {
        height: 100%;
        background: var(--brand-primary);
        transition: width 0.3s ease;
    }

    .progress-dot {
        position: relative;
        width: 20px;
        height: 20px;
        border-radius: var(--border-radius-circle);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
    }

    .progress-step--upcoming .progress-dot {
        border: 2px solid var(--gray-medium);
        background: var(--progress-bg, var(--white));
    }

    .progress-step--active .progress-dot {
        background: var(--brand-primary);
        box-shadow:
            inset 0 0 0 2px var(--brand-primary),
            inset 0 0 0 5px var(--progress-bg, var(--white));
    }

    .progress-step--completed .progress-dot {
        border: 2px solid var(--brand-primary);
        background: var(--brand-primary);
    }

    .progress-check-icon {
        color: var(--white);

        :deep(*) {
            stroke-width: 4;
        }
    }

    .progress-labels {
        display: flex;
        justify-content: space-between;
    }

    .progress-label {
        font-size: var(--font-xs);
        line-height: 1.2;
        color: var(--gray-medium);

        &:first-child {
            text-align: left;
        }

        &:nth-child(2) {
            text-align: center;
        }

        &:last-child {
            text-align: right;
        }
    }

    .progress-label--active {
        color: var(--brand-primary);
    }
</style>
